import { Alert, Modal, Typography, notification } from "antd";
import { Formik } from "formik";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import SecretFields from "./SecretFields";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { useMutation } from "shared/hooks/useApi";
import { bindInputProps } from "utils/helpers/input";
import { isValidIpRule } from "utils/helpers/validations";

const { Paragraph, Text } = Typography;

const ApiCredentialModal = ({ open, onClose, organization }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.BROKER);
  const API_KEY = t("API_KEY", { returnObjects: true });

  const [secret, setSecret] = useState(null);

  const [generate, { loading }] = useMutation(
    graphql.mutations.GENERATE_API_CREDENTIAL,
    {
      onCompleted: (data) => setSecret(get(data, "generateApiCredential")),
    },
  );

  const close = () => {
    setSecret(null);
    onClose();
  };

  const acceptIpEntries = (entries) => {
    const accepted = [];
    (entries || []).forEach((entry) => {
      const trimmed = String(entry).trim();
      if (!trimmed || accepted.includes(trimmed)) return;
      if (!isValidIpRule(trimmed)) {
        notification.warning({
          message: `${trimmed} — ${API_KEY.INVALID_IP}`,
        });
        return;
      }
      accepted.push(trimmed);
    });
    return accepted;
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      destroyOnClose
      title={API_KEY.TITLE}
    >
      {secret ? (
        <SecretFields secret={secret} onDone={close} />
      ) : (
        <Formik
          initialValues={{ label: "", allowedIps: [] }}
          onSubmit={({ label, allowedIps }) =>
            generate({
              variables: {
                data: {
                  label,
                  allowedIps,
                },
              },
            })
          }
        >
          {(formikProps) => (
            <>
              <Paragraph type="secondary">
                {API_KEY.ISSUING_FOR}{" "}
                <Text strong>{get(organization, "name", "-")}</Text>
              </Paragraph>

              <div className="m-bottom-16">
                <Input
                  label={API_KEY.PARTNER_LABEL}
                  placeholder={API_KEY.PARTNER_PLACEHOLDER}
                  {...bindInputProps({ name: "label", ...formikProps })}
                />
              </div>

              <div className="m-bottom-16 api-credential__ip-select">
                <Select
                  type="tag"
                  mode="tags"
                  options={[]}
                  notFoundContent={null}
                  suffixIcon={null}
                  tokenSeparators={[",", " "]}
                  label={API_KEY.ALLOWED_IPS}
                  placeholder={API_KEY.ALLOWED_IPS_PLACEHOLDER}
                  value={get(formikProps, "values.allowedIps", [])}
                  onChange={(entries) =>
                    formikProps.setFieldValue(
                      "allowedIps",
                      acceptIpEntries(entries),
                    )
                  }
                />
                <Text type="secondary">{API_KEY.ALLOWED_IPS_HELP}</Text>
              </div>

              {!get(formikProps, "values.allowedIps", []).length && (
                <Alert
                  type="info"
                  showIcon
                  className="m-bottom-16"
                  message={API_KEY.NO_IP_RESTRICTION}
                />
              )}

              <Button
                block
                loading={loading}
                disabled={!get(formikProps, "values.label", "").trim()}
                onClick={formikProps.handleSubmit}
                type="primary"
              >
                {API_KEY.GENERATE}
              </Button>
            </>
          )}
        </Formik>
      )}
    </Modal>
  );
};

export default ApiCredentialModal;
